import { useCallback, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Card, CardContent, CardHeader, Dialog, DialogActions, Grid, Theme } from '@mui/material';
import { useSearchManualApplicationsStore } from '@lib';
import { ManualApplicationSearchParams } from '@shared/api';
import { PermissionsUtils, RoutePaths, useRouteHook } from '@shared';
import { PermissionLevel, PermissionType } from '@shared/types';
import { DelayedLinearProgress, FileUpload } from '@widgets';
import { ManualApplicationSearchForm, ManualApplicationsTable } from './widgets';

const useStyles = makeStyles<Theme>((theme) => ({
  headerContainer: {
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    textAlign: 'center',
  },
  button: {
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(2),
    },
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

export const ManualApplicationsPage = () => {
  const { loading, manualApplications, searchManualApplications } = useSearchManualApplicationsStore();

  const [open, setOpen] = useState<boolean>(false);

  const { navigate } = useRouteHook();
  const classes = useStyles();

  const canUploadApplication =
    PermissionsUtils.hasPermission(PermissionType.IPPFILES, PermissionLevel.WRITE) &&
    PermissionsUtils.hasPermission(PermissionType.MANUAL_APPLICATIONS, PermissionLevel.ASSIGN);

  const onSubmit = useCallback(async (searchParams: ManualApplicationSearchParams) => searchManualApplications(searchParams), []);

  return (
    <Grid container alignContent='center'>
      <Grid item xs={12} md={3} xl={2}>
        <Card>
          <CardHeader title='Filter Manual Applications' />
          <CardContent style={{ paddingTop: 0 }}>
            <ManualApplicationSearchForm onSubmit={onSubmit} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={9} xl={10}>
        <Card>
          <DelayedLinearProgress loading={loading} />
          <div className={classes.headerContainer}>
            <CardHeader title='Manual Applications' />
            {canUploadApplication && (
              <Button variant='contained' color='primary' className={classes.button} onClick={() => setOpen(true)}>
                Upload New Application
              </Button>
            )}
          </div>

          {manualApplications && <ManualApplicationsTable loading={loading} data={manualApplications} />}
        </Card>
      </Grid>
      <Dialog disableEscapeKeyDown maxWidth='md' aria-labelledby='confirmation-dialog-title' open={open}>
        <FileUpload
          circles={PermissionsUtils.getUserCicles()}
          onUpload={(id?: string) => id && navigate(RoutePaths.manualApplicationsDetails.onNavigate(id))}
        />
        <DialogActions>
          <Button
            autoFocus
            onClick={() => {
              setOpen(false);
              searchManualApplications();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
